import { FileOutlined, FolderOutlined } from '@ant-design/icons';
import { ConfigProvider, Menu, type MenuProps, theme } from 'antd';
import { createStyles } from 'antd-style';
import { useMemo } from 'react';
import { PressRipple } from '@/components/PressRipple';
import { preloadRoute } from '@/config/preloadRoute';
import type { AppMenuItem } from '@/config/types';

const useMenuStyles = createStyles(({ css }) => ({
  root: css`
    /* 主题背景即时更新，文字与图标不再额外延迟变色；保留折叠几何动画。 */
    &&& .ant-menu-title-content {
      transition-property: opacity, margin;
    }
    &&& .ant-menu-item-icon, &&& .anticon {
      transition-property: font-size, margin;
    }
    &&& .ant-menu-title-content > a {
      transition: none;
    }
  `,
  sidebarMotion: css`
    /* ProLayout owns the sidebar width (0.2s ease). Avoid a second width
       animation and finish menu geometry in the same interval. */
    &&& { transition: none; }
    &&& .ant-menu-item,
    &&& .ant-menu-submenu-title,
    &&& .ant-menu-title-content,
    &&& .ant-menu-item-icon,
    &&& .anticon,
    &&& .ant-menu-submenu-arrow {
      transition-duration: 0.2s;
      transition-timing-function: ease;
    }
    @media (prefers-reduced-motion: reduce) {
      &&& .ant-menu-item,
      &&& .ant-menu-submenu-title,
      &&& .ant-menu-title-content,
      &&& .ant-menu-item-icon,
      &&& .anticon,
      &&& .ant-menu-submenu-arrow { transition: none; }
    }
  `,
  grid: css`
    .service-section > ul.ant-menu-sub::before, .service-section > ul.ant-menu-sub::after { content: none; }
    /* Let AntD hide closed submenus after their collapse animation. */
    .service-section > ul.ant-menu-sub:not(.ant-menu-hidden) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .service-section > ul > .ant-menu-submenu { grid-column: 1 / -1; }
    .service-section > ul > .ant-menu-item { padding-inline: 12px !important; min-width: 0; }
    .service-section > ul > .ant-menu-item .anticon { display: none; }
  `,
  rootGrid: css`
    &::before, &::after { content: none; }
    display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
    > .ant-menu-submenu { grid-column: 1 / -1; }
    > .ant-menu-item { padding-inline: 12px !important; min-width: 0; }
    > .ant-menu-item .anticon { display: none; }
  `,
}));

export function NavigationMenu({
  items,
  selectedKeys,
  openKeys,
  onOpenChange,
  onNavigate,
  collapsed = false,
  mode = 'inline',
  serviceGrid = false,
  rootGrid = false,
}: {
  items: AppMenuItem[];
  selectedKeys: string[];
  openKeys?: string[];
  onOpenChange?: (keys: string[]) => void;
  onNavigate: (path: string) => void;
  collapsed?: boolean;
  mode?: 'horizontal' | 'inline';
  serviceGrid?: boolean;
  rootGrid?: boolean;
}) {
  const { token } = theme.useToken();
  const { styles } = useMenuStyles();
  const { menuItems, destinations } = useMemo(() => {
    const destinations = new Map<string, string>();
    function toItems(nodes: AppMenuItem[], depth = 0): MenuProps['items'] {
      for (const item of nodes)
        if (item.path && !item.children) destinations.set(item.key, item.path);
      return nodes.map((item) => ({
        key: item.key,
        icon:
          item.icon ??
          (mode === 'inline' && depth === 0 ? (
            item.children ? (
              <FolderOutlined aria-hidden />
            ) : (
              <FileOutlined aria-hidden />
            )
          ) : undefined),
        title: item.name,
        className: item.children ? 'service-section' : undefined,
        label:
          item.path && !item.children ? (
            <a
              href={`${import.meta.env.BASE_URL}${item.path.slice(1)}`}
              onMouseEnter={() => preloadRoute(item.path ?? '')}
              onFocus={() => preloadRoute(item.path ?? '')}
              onPointerDown={() => preloadRoute(item.path ?? '')}
              onClick={(event) => {
                if (
                  event.button === 0 &&
                  !event.ctrlKey &&
                  !event.metaKey &&
                  !event.shiftKey &&
                  !event.altKey
                ) {
                  event.preventDefault();
                }
              }}
            >
              {item.name}
            </a>
          ) : (
            item.name
          ),
        children: item.children ? toItems(item.children, depth + 1) : undefined,
      }));
    }
    return { menuItems: toItems(items), destinations };
  }, [items, mode]);
  return (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            // ProLayout's menu label uses marginXS (8px), native Menu defaults to 10px.
            iconMarginInlineEnd: token.marginXS,
            dangerItemActiveBg: 'transparent',
            itemActiveBg: 'transparent',
            itemBg: token.colorBgContainer,
            // 接管 ProLayout 的次级文字默认值，所有导航入口共用管理端主文字色。
            itemColor: token.colorText,
            itemBorderRadius: 10,
            itemHeight: 40,
            itemHoverBg: token.colorBgTextHover,
            itemHoverColor: token.colorText,
            itemMarginBlock: 4,
            itemMarginInline: 4,
            itemSelectedBg: token.controlItemBgActive,
            itemSelectedColor: token.colorPrimary,
            subMenuItemBg: token.colorFillAlter,
            subMenuItemBorderRadius: 10,
            subMenuItemSelectedColor: token.colorPrimary,
            horizontalItemBorderRadius: token.borderRadius,
            ...(mode === 'horizontal'
              ? {
                  activeBarBorderWidth: 0,
                  activeBarHeight: 3,
                }
              : {}),
          },
        },
      }}
    >
      {/* 保留 Menu 实例，仅清理折叠切换时的波纹。 */}
      <PressRipple resetKey={mode === 'inline' && collapsed}>
        <Menu
          items={menuItems}
          onFocusCapture={(event) => {
            const element = event.target as HTMLElement;
            const anchor = element
              .closest('[role="menuitem"]')
              ?.querySelector('a[href]');
            const path = anchor?.getAttribute('href');
            if (path) preloadRoute(path);
          }}
          styles={{
            // rc-menu keeps closed popup DOM for reuse. Hide it immediately when
            // returning to inline mode, without destroying the menu mid-motion.
            popup: { root: mode === 'inline' && !collapsed ? { display: 'none' } : {} },
          }}
          onClick={({ key, domEvent }) => {
            if (
              domEvent.ctrlKey ||
              domEvent.metaKey ||
              domEvent.shiftKey ||
              domEvent.altKey
            )
              return;
            const destination = destinations.get(key);
            if (destination) onNavigate(destination);
          }}
          selectedKeys={selectedKeys}
          mode={mode}
          {...(mode === 'inline'
            ? {
                inlineCollapsed: collapsed,
                openKeys: collapsed ? undefined : openKeys,
                // 折叠时 Menu 会关闭浮层，不能据此清空展开状态下的路由分组。
                onOpenChange: collapsed ? undefined : onOpenChange,
              }
            : {})}
          className={[
            styles.root,
            mode === 'inline' ? styles.sidebarMotion : '',
            serviceGrid ? styles.grid : '',
            serviceGrid && rootGrid ? styles.rootGrid : '',
          ].join(' ')}
          style={{
            // 保留整行按压与键盘焦点，仅关闭浏览器附加的文字触摸高亮。
            WebkitTapHighlightColor: 'transparent',
            border: 0,
            background: token.colorBgContainer,
            minWidth: 0,
            width: '100%',
          }}
        />
      </PressRipple>
    </ConfigProvider>
  );
}
